import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtDate, ROLE_LABEL } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import { DetailShell, InfoRow, InfoGrid } from "@/components/DetailShell";
import { BtnPrimary, BtnSecondary, Empty, Field, Input } from "@/components/primitives";
import PhotoUploader from "@/components/PhotoUploader";
import DocumentUploader from "@/components/DocumentUploader";
import { toast } from "sonner";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "contact", label: "Contact & Emergency" },
  { key: "documents", label: "Documents" },
];

export default function UserProfile() {
  const { id } = useParams();
  const { user: current } = useAuth();
  const [u, setU] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const isSelf = current?.id === id;
  const isAdmin = current?.role === "admin";
  const canEdit = isSelf || isAdmin;

  async function load() {
    try {
      const { data } = await api.get(`/users/${id}`);
      setU(data);
      setForm(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function save() {
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        address: form.address,
        photo_url: form.photo_url,
        permit_url: form.permit_url,
        id_photo_url: form.id_photo_url,
        emergency_contact_name: form.emergency_contact_name,
        emergency_contact_phone: form.emergency_contact_phone,
        emergency_contact_relation: form.emergency_contact_relation,
        documents: form.documents,
        notes: form.notes,
      };
      await api.patch(`/users/${id}/profile`, payload);
      toast.success("Profile saved");
      setEdit(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function updateDocs(list) {
    try {
      await api.patch(`/users/${id}/profile`, { documents: list });
      setU({ ...u, documents: list });
      setForm({ ...form, documents: list });
    } catch (e) { toast.error(formatError(e)); }
  }

  if (loading) return <div className="phizo-label">Loading profile…</div>;
  if (!u) return <Empty label="User not found." />;

  return (
    <DetailShell
      overline="ACCOUNT / PROFILE"
      title={u.name}
      subtitle={`${u.username || "—"} · ${ROLE_LABEL[u.role] || u.role}`}
      backTo={isAdmin ? "/users" : "/"}
      testId="user-profile"
      actions={
        canEdit && (
          edit ? (
            <>
              <BtnSecondary onClick={() => { setEdit(false); setForm(u); }}>Cancel</BtnSecondary>
              <BtnPrimary onClick={save} data-testid="user-profile-save">Save</BtnPrimary>
            </>
          ) : (
            <BtnSecondary onClick={() => setEdit(true)} data-testid="user-profile-edit">Edit Profile</BtnSecondary>
          )
        )
      }
    >
      <div className="mb-6 border-b border-[#e4e4e7] flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`user-tab-${t.key}`}
            className={`px-4 py-2 text-sm font-semibold uppercase tracking-wider border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-[#09090b] text-[#09090b]" : "border-transparent text-[#a1a1aa] hover:text-[#52525b]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <InfoGrid cols={2}>
              <InfoRow label="Username" value={u.username} />
              <InfoRow label="Email" value={u.email || "—"} />
              <InfoRow label="Full Name" value={u.name} />
              <InfoRow label="Role" value={ROLE_LABEL[u.role] || u.role} />
              <InfoRow label="Created" value={fmtDate(u.created_at)} />
              <InfoRow label="Phone" value={u.phone || "—"} />
              <InfoRow label="Address" value={u.address || "—"} />
            </InfoGrid>
            {edit && (
              <div className="mt-4 phizo-card p-4 space-y-3">
                <div className="phizo-label">Edit basic details</div>
                <Field label="Full Name"><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                <Field label="Phone"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-phone" /></Field>
                <Field label="Address"><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
              </div>
            )}
          </div>
          <div className="phizo-card p-4">
            <div className="phizo-label mb-3">Profile Photo</div>
            {canEdit ? (
              <PhotoUploader
                value={form.photo_url}
                onChange={(url) => { setForm({ ...form, photo_url: url }); }}
                category="user_profile"
                size={128}
                testId="user-photo"
              />
            ) : u.photo_url ? (
              <img src={u.photo_url} alt={u.name} className="w-32 h-32 object-cover" />
            ) : <div className="text-sm text-[#a1a1aa]">No photo uploaded.</div>}
            {canEdit && (
              <BtnPrimary className="mt-3 w-full" onClick={save}>Save photo</BtnPrimary>
            )}
          </div>
        </div>
      )}

      {tab === "contact" && (
        <div className="phizo-card p-4">
          <div className="phizo-label mb-3">Emergency Contact</div>
          {edit ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Name"><Input value={form.emergency_contact_name || ""} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} data-testid="user-ec-name" /></Field>
              <Field label="Phone"><Input value={form.emergency_contact_phone || ""} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} data-testid="user-ec-phone" /></Field>
              <Field label="Relation"><Input value={form.emergency_contact_relation || ""} onChange={(e) => setForm({ ...form, emergency_contact_relation: e.target.value })} data-testid="user-ec-relation" /></Field>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div><div className="phizo-label">Name</div><div>{u.emergency_contact_name || "—"}</div></div>
              <div><div className="phizo-label">Phone</div><div>{u.emergency_contact_phone || "—"}</div></div>
              <div><div className="phizo-label">Relation</div><div>{u.emergency_contact_relation || "—"}</div></div>
            </div>
          )}
        </div>
      )}

      {tab === "documents" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="phizo-card p-4">
            <div className="phizo-label mb-3">Driver / Operator Permit</div>
            {u.permit_url ? (
              <a href={u.permit_url} target="_blank" rel="noreferrer" className="text-sm text-[#002FA7] hover:underline mono break-all block mb-2">{u.permit_url}</a>
            ) : <div className="text-sm text-[#a1a1aa] mb-2">No permit uploaded.</div>}
            {canEdit && (
              <DocumentUploader
                value={u.permit_url ? [{ name: "Current permit", url: u.permit_url, type: "application/pdf" }] : []}
                onChange={(list) => {
                  const last = list[list.length - 1];
                  const url = last ? last.url : null;
                  api.patch(`/users/${id}/profile`, { permit_url: url })
                    .then(() => { toast.success("Permit updated"); load(); })
                    .catch((e) => toast.error(formatError(e)));
                }}
                category="user_permit"
                testId="user-permit"
              />
            )}
          </div>

          <div className="phizo-card p-4">
            <div className="phizo-label mb-3">National ID / NRC Photo</div>
            {u.id_photo_url ? (
              <a href={u.id_photo_url} target="_blank" rel="noreferrer" className="text-sm text-[#002FA7] hover:underline mono break-all block mb-2">{u.id_photo_url}</a>
            ) : <div className="text-sm text-[#a1a1aa] mb-2">No ID photo uploaded.</div>}
            {canEdit && (
              <DocumentUploader
                value={u.id_photo_url ? [{ name: "Current ID", url: u.id_photo_url, type: "image/jpeg" }] : []}
                onChange={(list) => {
                  const last = list[list.length - 1];
                  const url = last ? last.url : null;
                  api.patch(`/users/${id}/profile`, { id_photo_url: url })
                    .then(() => { toast.success("ID updated"); load(); })
                    .catch((e) => toast.error(formatError(e)));
                }}
                category="user_id"
                testId="user-id"
              />
            )}
          </div>

          <div className="lg:col-span-2 phizo-card p-4">
            <div className="phizo-label mb-3">Additional Documents</div>
            <DocumentUploader
              value={u.documents || []}
              onChange={updateDocs}
              category="user_docs"
              testId="user-docs"
              disabled={!canEdit}
            />
          </div>
        </div>
      )}
    </DetailShell>
  );
}
